import React, { memo } from 'react'
import type { ColumnVisibility, SortColumn } from './types'
import { COLUMN_MIN_WIDTHS } from './utils'
import type { MarketCustomColumn } from '../../types/database'

interface MarketTableHeaderProps {
  tableType: 'for-sale' | 'sold' | 'removed'
  columnVisibility: ColumnVisibility
  columnWidths: Map<string, number>
  customColumns: MarketCustomColumn[]
  colorScheme: {
    subHeader: string
    subHeaderText: string
  }
  headerBorderClass: string
  handleSort: (column: SortColumn) => void
}

// Helper to get width style with both percentage and minimum width
const getWidthStyle = (key: string, columnWidths: Map<string, number>) => ({
  width: `${columnWidths.get(key) || 5}%`,
  minWidth: `${COLUMN_MIN_WIDTHS[key] || 60}px`
})

export const MarketTableColGroup = memo(function MarketTableColGroup({
  tableType,
  columnVisibility: cv,
  columnWidths,
  customColumns,
}: Pick<MarketTableHeaderProps, 'tableType' | 'columnVisibility' | 'columnWidths' | 'customColumns'>) {
  const cols: React.ReactNode[] = []
  let colIndex = 0

  const hideSerialNumber = cv.hideSerialNumber ?? false
  const hideOwner = cv.hideOwner ?? false
  const hideBroker = cv.hideBroker ?? false
  const hideYearMfr = cv.hideYearMfr ?? false
  const hideAftt = cv.hideAftt ?? false
  const hideEmp = cv.hideEmp ?? false
  const hideDmg = cv.hideDmg ?? false
  const hidePax = cv.hidePax ?? false
  const hidePaintInterior = cv.hidePaintInterior ?? false
  const hideOptions = cv.hideOptions ?? false
  const hideDateListed = cv.hideDateListed ?? false
  const hideJnAsk = cv.hideJnAsk ?? false
  const hideAskingPrice = cv.hideAskingPrice ?? false
  const hideExternalColumns = cv.hideExternalColumns ?? false
  const hideRelativeValue = cv.hideRelativeValue ?? false

  if (!hideSerialNumber) cols.push(<col key={colIndex++} style={getWidthStyle('serial', columnWidths)} />)
  if (!hideOwner) cols.push(<col key={colIndex++} style={getWidthStyle('owner', columnWidths)} />)
  if (!hideBroker) cols.push(<col key={colIndex++} style={getWidthStyle('broker', columnWidths)} />)
  if (!hideYearMfr) cols.push(<col key={colIndex++} style={getWidthStyle('year', columnWidths)} />)
  if (!hideAftt) cols.push(<col key={colIndex++} style={getWidthStyle('aftt', columnWidths)} />)
  if (!hideEmp) cols.push(<col key={colIndex++} style={getWidthStyle('emp', columnWidths)} />)
  if (!hideDmg) cols.push(<col key={colIndex++} style={getWidthStyle('damage', columnWidths)} />)
  if (!hidePax) cols.push(<col key={colIndex++} style={getWidthStyle('pax', columnWidths)} />)
  if (!hidePaintInterior) cols.push(<col key={colIndex++} style={getWidthStyle('paintInterior', columnWidths)} />)

  // Custom columns
  customColumns.forEach((_, i) => {
    cols.push(<col key={`custom-${i}`} style={getWidthStyle(`custom_${i}`, columnWidths)} />)
  })

  if (!hideOptions) cols.push(<col key="options" style={getWidthStyle('options', columnWidths)} />)

  // Type-specific columns
  if (tableType === 'for-sale') {
    if (!hideDateListed) cols.push(<col key="dateListed" style={getWidthStyle('dateListed', columnWidths)} />)
    if (!hideJnAsk) cols.push(<col key="jnAsk" style={getWidthStyle('jnAsk', columnWidths)} />)
    if (!hideAskingPrice) cols.push(<col key="askingTake" style={getWidthStyle('askingTake', columnWidths)} />)
    if (!hideExternalColumns) cols.push(<col key="externalPrices" style={getWidthStyle('externalPrices', columnWidths)} />)
  } else if (tableType === 'sold') {
    cols.push(<col key="datesSold" style={getWidthStyle('datesSold', columnWidths)} />)
    if (!hideAskingPrice) cols.push(<col key="pricesSold" style={getWidthStyle('pricesSold', columnWidths)} />)
    if (!hideExternalColumns) cols.push(<col key="externalPrices" style={getWidthStyle('externalPrices', columnWidths)} />)
  } else if (tableType === 'removed') {
    cols.push(<col key="dateRemoved" style={getWidthStyle('dateRemoved', columnWidths)} />)
    if (!hideExternalColumns) cols.push(<col key="externalPrices" style={getWidthStyle('externalPrices', columnWidths)} />)
  }

  if (!hideRelativeValue) cols.push(<col key="relativeValue" style={getWidthStyle('relativeValue', columnWidths)} />)

  return <colgroup>{cols}</colgroup>
})

export const MarketTableHeaderRow = memo(function MarketTableHeaderRow({
  tableType,
  columnVisibility: cv,
  customColumns,
  colorScheme,
  headerBorderClass,
  handleSort,
}: Omit<MarketTableHeaderProps, 'columnWidths'>) {
  const hideSerialNumber = cv.hideSerialNumber ?? false
  const hideOwner = cv.hideOwner ?? false
  const hideBroker = cv.hideBroker ?? false
  const hideYearMfr = cv.hideYearMfr ?? false
  const hideAftt = cv.hideAftt ?? false
  const hideEmp = cv.hideEmp ?? false
  const hideDmg = cv.hideDmg ?? false
  const hidePax = cv.hidePax ?? false
  const hidePaintInterior = cv.hidePaintInterior ?? false
  const hideOptions = cv.hideOptions ?? false
  const hideDateListed = cv.hideDateListed ?? false
  const hideJnAsk = cv.hideJnAsk ?? false
  const hideAskingPrice = cv.hideAskingPrice ?? false
  const hideExternalColumns = cv.hideExternalColumns ?? false
  const hideRelativeValue = cv.hideRelativeValue ?? false

  const thClass = `px-2 py-2 text-center text-xs font-medium ${colorScheme.subHeaderText} uppercase tracking-wider`
  const sortableThClass = `${thClass} cursor-pointer hover:bg-opacity-80 select-none`

  return (
    <tr>
      {!hideSerialNumber && (
        <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('serial')}>
          <div className="flex items-center justify-center">
            <div><div>S/N</div><div>Reg</div></div>
          </div>
        </th>
      )}
      {!hideOwner && (
        <th className={`${thClass} ${headerBorderClass}`}>
          <div>Aircraft</div><div>Owner</div>
        </th>
      )}
      {!hideBroker && (
        <th className={`${thClass} ${headerBorderClass}`}>
          <div>Aircraft</div><div>Agent</div>
        </th>
      )}
      {!hideYearMfr && (
        <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('year')}>
          <div className="flex items-center justify-center">
            <div><div>YOM</div><div>EIS</div></div>
          </div>
        </th>
      )}
      {!hideAftt && (
        <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('aftt')}>
          <div className="flex items-center justify-center">
            <div><div>AFTT</div><div>S/New</div></div>
          </div>
        </th>
      )}
      {!hideEmp && (
        <th className={`${thClass} ${headerBorderClass}`}>
          <div>Engine</div><div>Prog</div>
        </th>
      )}
      {!hideDmg && (
        <th className={`${thClass} ${headerBorderClass}`}>DMG</th>
      )}
      {!hidePax && (
        <th className={`${thClass} ${headerBorderClass}`}>PAX</th>
      )}
      {!hidePaintInterior && (
        <th className={`${thClass} ${headerBorderClass}`}>
          <div>Paint</div><div>Interior</div>
        </th>
      )}
      {/* Custom Columns */}
      {customColumns.map((column) => {
        const titleWords = column.column_title.trim().split(/\s+/)
        return (
          <th
            key={column.id}
            className={`${sortableThClass} ${headerBorderClass}`}
            onClick={() => handleSort(`custom_${column.column_index}` as SortColumn)}
          >
            <div className="flex items-center justify-center">
              <div>
                {titleWords.map((word, idx) => (
                  <div key={idx}>{word}</div>
                ))}
              </div>
            </div>
          </th>
        )
      })}
      {!hideOptions && (
        <th className={`${thClass} ${headerBorderClass}`}>
          <div>Options &amp;</div><div>Additional Equipment</div>
        </th>
      )}
      {/* Type-specific columns */}
      {tableType === 'for-sale' && (
        <>
          {!hideDateListed && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('date_listed')}>
              <div className="flex items-center justify-center">
                <div><div>Date Listed</div><div>DOM</div></div>
              </div>
            </th>
          )}
          {!hideJnAsk && (
            <th className={`${thClass} ${headerBorderClass}`}>
              <div>JETNET</div><div>Ask Price</div>
            </th>
          )}
          {!hideAskingPrice && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('asking')}>
              <div className="flex items-center justify-center">
                <div><div>Ask Price</div><div>Take Price</div></div>
              </div>
            </th>
          )}
          {!hideExternalColumns && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('external_asking')}>
              <div className="flex items-center justify-center">
                <div><div>Adj. Ask</div><div>Adj. Take</div></div>
              </div>
            </th>
          )}
        </>
      )}
      {tableType === 'sold' && (
        <>
          <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('date_sold')}>
            <div className="flex items-center justify-center">
              <div><div>Listed</div><div>Sold</div></div>
            </div>
          </th>
          {!hideAskingPrice && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('sold')}>
              <div className="flex items-center justify-center">
                <div><div>Ask Price</div><div>Sold Price</div></div>
              </div>
            </th>
          )}
          {!hideExternalColumns && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('external_asking')}>
              <div className="flex items-center justify-center">
                <div><div>Adj. Ask</div><div>Adj. Sold</div></div>
              </div>
            </th>
          )}
        </>
      )}
      {tableType === 'removed' && (
        <>
          <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('date_removed')}>
            <div className="flex items-center justify-center">Removed</div>
          </th>
          {!hideExternalColumns && (
            <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('external_asking')}>
              <div className="flex items-center justify-center">
                <div><div>Adj. Ask</div><div>Adj. Take</div></div>
              </div>
            </th>
          )}
        </>
      )}
      {/* Relative Value column - always last */}
      {!hideRelativeValue && (
        <th className={`${sortableThClass} ${headerBorderClass}`} onClick={() => handleSort('relative_value')}>
          <div className="flex items-center justify-center">
            <div><div>Comparative</div><div>Values</div></div>
          </div>
        </th>
      )}
    </tr>
  )
})

